import { checkIfCNPJIsValid, checkIfCPFIsValid } from 'v3/utils/documentValidator';
import * as Yup from 'yup';
import moment from 'moment';
import { rntrcExpDateSchema, rntrcIssueDateSchema } from 'v3/utils/rntrcValidation';

export const dataRegistrationSchema = Yup.object().shape({
  rntrc_issue_date: rntrcIssueDateSchema(),
  rntrc_exp_date: rntrcExpDateSchema('rntrc_issue_date'),
  personType: Yup.object().shape({
    name: Yup.string().required('Tipo de pessoa obrigatório!'),
  }),
  cgccpf: Yup.string()
    .required('CPF/CNPJ obrigatório')
    .nullable()
    .when('personType', {
      is: (value) => value?.name === 'Jurídica',
      then: (schema) =>
        schema.test('invalid-document', 'CNPJ inválido', (value) => checkIfCNPJIsValid(value)),
      otherwise: (schema) =>
        schema.test('invalid-document', 'CPF inválido', (value) => checkIfCPFIsValid(value)),
    }),

  pispasep: Yup.string()
    .nullable()
    .when('personType', {
      is: (value) => value?.name === 'Física',
      then: (schema) =>
        schema.required('Pis/Pasep obrigatório').transform((currentValue) => {
          return currentValue === null ? undefined : currentValue;
        }),
    }),
  payment_method: Yup.string().when('is_driver', {
    is: (value) => value,
    then: (schema) => schema.required('Método de pagamento obrigatório'),
  }),
  social_name: Yup.string().nullable().required('Nome obrigatório!'),
  phone: Yup.string()
    .nullable()
    .min(9, 'Telefone deve ter pelo menos 9 dígitos')
    .max(15, 'Telefone não pode ter mais de 11 dígitos')
    .required('Telefone obrigatório'),
});

export const travelShippingCompanyRegistrationSchema = Yup.object().shape({
  type_id: Yup.number().required('Tipo de pessoa obrigatório'),
  cgccpf_unmasked: Yup.string()
    .required('CPF/CNPJ obrigatório')
    .nullable()
    .when('type_id', {
      is: (value) => value === 3,
      then: (schema) =>
        schema.test('invalid-document', 'CNPJ inválido', (value) => checkIfCNPJIsValid(value)),
      otherwise: (schema) =>
        schema.test('invalid-document', 'CPF inválido', (value) => checkIfCPFIsValid(value)),
    }),
  social_name: Yup.string().nullable().required('Nome obrigatório!'),
  phone: Yup.string()
    .nullable()
    .min(9, 'Telefone deve ter pelo menos 9 dígitos')
    .max(15, 'Telefone não pode ter mais de 11 dígitos')
    .required('Telefone obrigatório'),
  is_driver: Yup.boolean().required('Motorista obrigatório'),
});

export const travelShippingCompanyDetailsSchema = Yup.object().shape({
  type_id: Yup.number().required('Tipo de pessoa obrigatório'),
  cgccpf_unmasked: Yup.string()
    .required('CPF/CNPJ obrigatório')
    .nullable()
    .when('type_id', {
      is: (value) => value === 3,
      then: (schema) =>
        schema.test('invalid-document', 'CNPJ inválido', (value) => checkIfCNPJIsValid(value)),
      otherwise: (schema) =>
        schema.test('invalid-document', 'CPF inválido', (value) => checkIfCPFIsValid(value)),
    }),
  social_name: Yup.string().nullable().required('Nome obrigatório!'),
  shipment_link_id: Yup.number().nullable().required('Vínculo transportador obrigatório'),
  phone_unmasked: Yup.string(),
  phone: Yup.string()
    .nullable()
    .min(9, 'Telefone deve ter pelo menos 9 dígitos')
    .max(15, 'Telefone não pode ter mais de 11 dígitos')
    .required('Telefone obrigatório'),
});

export const dataSchema = Yup.object().shape({
  emission_start_date: Yup.string()
    .nullable()
    .test(
      'valid-date',
      'Data de início da emissão de contra CT-e inválida',
      (value) =>
        !value || !value.replace(/[_/\s]/g, '') || moment(value, 'DD/MM/YYYY', true).isValid(),
    ),
  rntrc_issue_date: rntrcIssueDateSchema(),
  rntrc_exp_date: rntrcExpDateSchema('rntrc_issue_date'),
  personType: Yup.object().shape({
    name: Yup.string().required('Tipo de pessoa obrigatório!'),
  }),
  cgccpf: Yup.string()
    .required('CPF/CNPJ obrigatório')
    .nullable()
    .when('personType', {
      is: (value) => value?.name === 'Jurídica',
      then: (schema) =>
        schema.test('invalid-document', 'CNPJ inválido', (value) => checkIfCNPJIsValid(value)),
      otherwise: (schema) =>
        schema.test('invalid-document', 'CPF inválido', (value) => checkIfCPFIsValid(value)),
    }),
  pispasep: Yup.string()
    .nullable()
    .when('personType', {
      is: (value) => value?.name === 'Física',
      then: (schema) =>
        schema.required('Pis/Pasep obrigatório').transform((currentValue) => {
          return currentValue === null ? undefined : currentValue;
        }),
    }),
  ie: Yup.string()
    .nullable()
    .when('personType', {
      is: (value) => value?.name === 'Jurídica',
      then: (schema) =>
        schema.required('Inscrição estadual obrigatória').transform((currentValue) => {
          return currentValue === null ? undefined : currentValue;
        }),
    }),
  fantasy_name: Yup.string()
    .nullable()
    .when('personType', {
      is: (value) => value?.name === 'Jurídica',
      then: (schema) =>
        schema.required('Nome fantasia obrigatório').transform((currentValue) => {
          return currentValue === null ? undefined : currentValue;
        }),
    }),
  social_name: Yup.string().nullable().required('Nome obrigatório!'),
  phone: Yup.string()
    .nullable()
    .min(9, 'Telefone deve ter pelo menos 9 dígitos')
    .max(15, 'Telefone não pode ter mais de 11 dígitos')
    .required('Telefone obrigatório'),
});

export const addressSchema = Yup.object().shape({
  address: Yup.string().required('Endereço obrigatório').typeError('Endereço obrigatório'),

  city_name: Yup.string().required('Cidade obrigatória').typeError('Cidade obrigatória'),

  uf_name: Yup.string().required('Estado obrigatório').typeError('Estado obrigatório'),

  neighborhood: Yup.string().required('Bairro obrigatório').typeError('Bairro obrigatório'),

  zip_code: Yup.string().required('CEP obrigatório').typeError('CEP obrigatório'),

  country: Yup.object().nullable().required('País obrigatório').typeError('País obrigatório'),

  numberless: Yup.boolean(),

  number: Yup.string()
    .nullable()
    .when('numberless', {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Número obrigatório').typeError('Número obrigatório'),
    }),
});

export const shippingCompanyRegistrationSchema = Yup.object().shape({
  data: dataRegistrationSchema,
  address: addressSchema
    .nullable()
    .required('Endereço obrigatório')
    .typeError('Endereço obrigatório'),
});

export const shippingCompanySchema = Yup.object().shape({
  data: dataSchema,
  address: addressSchema,
});
